import { type TFunction } from "i18next"

import type {
  AppointmentListRow,
  GlobalMapListDisplayRow,
  ListRow,
  ProjectOrLeadListRow,
  SubcontractorListRow
} from "../redux/global-map.types.ts"

// A shared, honest "no value" marker for a field this row's entity simply
// has no data for (never fabricated) — matches the literal character the
// rest of this codebase already uses for the same purpose (see
// `constants/placeholders.ts`'s `empty`, a plain hyphen); this is the
// longer em dash specifically because that is what the brief's own
// row-mapping table and failing test pin.
const EM_DASH = "—"

// `ListRowLocation.address` is one already-formatted string (see
// `_serialize_location`'s `formatted_address` fallback on the backend) —
// there is no separate street/city-state field to read two lines from. A
// comma is the one structural signal the string itself carries (a US
// formatted address's own convention), so it is used to split "street" from
// "city, state" for the two-line Location cell without inventing data the
// backend never sent.
function splitAddress(
  address: string | null | undefined
): [string | undefined, string | undefined] {
  if (!address) return [undefined, undefined]
  const commaIndex = address.indexOf(",")
  if (commaIndex === -1) return [address, undefined]
  return [
    address.slice(0, commaIndex).trim(),
    address.slice(commaIndex + 1).trim() || undefined
  ]
}

function projectOrLeadDisplayRow(
  row: ProjectOrLeadListRow,
  t: TFunction
): GlobalMapListDisplayRow {
  const isLead = row.entity_type === "lead"
  const [locationPrimary, locationSecondary] = splitAddress(
    row.location.address
  )
  // Row-mapping table, corrected against the real backend shape
  // (`LeadQuickViewSerializer.get_people`/`ProjectQuickViewSerializer.get_people`
  // in ezbuild_back): a Lead's assigned sales manager is emitted under the
  // key `owner` (never `salesperson` — the key the pre-fix version of this
  // file read, which meant `ownerName` was `undefined` for every real Lead
  // row). A Project's owner mirrors the export's own documented priority
  // (`_export_row_project` in `ezbuild_back/apps/global_map/views.py`):
  // project manager first, falling back to the salesperson when there is no
  // PM, so the List column and the exported sheet can never disagree about
  // the same record. `ownerRole` follows whichever field actually matched,
  // rather than always reading "Project Manager" — a project shown via the
  // salesperson fallback is honestly labelled as one. `ListRow` carries no
  // `project_type`/lead-type field at all (see this file's own
  // `GlobalMapListDisplayRow` doc comment and the Increment 4 KB decision it
  // cites) — Ruled unreachable rather than typed around, so `secondaryText`
  // is the reference alone.
  let ownerName: string | undefined
  let ownerRole: string
  if (isLead) {
    ownerName = row.people.owner
    ownerRole = t("globalMap.list.ownerRole.salesperson")
  } else if (row.people.project_manager) {
    ownerName = row.people.project_manager
    ownerRole = t("globalMap.list.ownerRole.projectManager")
  } else {
    ownerName = row.people.salesperson
    ownerRole = t("globalMap.list.ownerRole.salesperson")
  }

  return {
    id: row.id,
    entityType: row.entity_type,
    name: row.title ?? t("globalMap.list.untitled"),
    secondaryText: row.reference,
    locationPrimary,
    locationSecondary,
    locationState: row.location.state,
    statusLabel: row.status_label,
    owner: ownerName ? { name: ownerName, role: ownerRole } : undefined,
    datePrimary: row.relevant_date ?? EM_DASH,
    dateLabel: isLead
      ? t("globalMap.list.dateLabel.nextFollowUp")
      : t("globalMap.list.dateLabel.startDate"),
    // No estimate/contract-value field exists yet on `ListRow` — confirmed
    // against the real `ProjectQuickViewSerializer`/`LeadQuickViewSerializer`
    // in `ezbuild_back` (neither carries `sales_price`/an estimate
    // equivalent on this endpoint), and `quick-view-fields.ts`'s own doc
    // comment independently names "Estimated Value" as a field "this app
    // doesn't have yet" for the same map pin card. Surfacing it needs a
    // backend addition outside this (frontend-only) worktree — left as
    // `undefined` (renders the Value column's own missing-value state)
    // rather than fabricated. See the task report for the flagged follow-up.
    valuePrimary: undefined,
    valueLabel: isLead
      ? t("globalMap.list.valueLabel.estValue")
      : t("globalMap.list.valueLabel.contractValue")
  }
}

function subcontractorDisplayRow(
  row: SubcontractorListRow,
  t: TFunction
): GlobalMapListDisplayRow {
  return {
    id: row.id,
    entityType: "subcontractor",
    name: row.name,
    secondaryText: row.specialization_label ?? undefined,
    // Ruled Decision 5 — a Subcontractor is never geocoded, so this is
    // genuinely "missing" rather than a placeholder awaiting real data.
    locationState: "missing",
    statusLabel: row.status_label,
    // Ruled Decision 6 — no owner concept exists for a Subcontractor row.
    owner: undefined,
    datePrimary: EM_DASH,
    dateLabel: t("globalMap.list.dateLabel.date"),
    // Ruled Decision 7 — one line only.
    valuePrimary: t("globalMap.list.activeProjectsCount", {
      count: row.active_project_count
    }),
    valueLabel: t("globalMap.list.valueLabel.valueDetails")
  }
}

function appointmentDisplayRow(
  row: AppointmentListRow,
  t: TFunction
): GlobalMapListDisplayRow {
  return {
    id: row.id,
    entityType: "appointment",
    name: row.title ?? t("globalMap.list.untitled"),
    secondaryText: row.related_record_title ?? undefined,
    locationPrimary: row.address ?? undefined,
    // An Appointment carries a plain address string with no geocode
    // precision at all (unlike a Project/Lead's `location.state`) — read
    // as "mapped" (no badge) when an address exists, "missing" (the same
    // shared badge every other entity uses) when it doesn't.
    locationState: row.address ? "mapped" : "missing",
    statusLabel: row.status_label,
    owner: row.assigned_user_name
      ? { name: row.assigned_user_name }
      : undefined,
    datePrimary: row.start_at,
    dateLabel: t("globalMap.list.dateLabel.date"),
    valuePrimary: row.related_record_title ?? undefined,
    valueLabel: t("globalMap.list.valueLabel.relatedRecord")
  }
}

/**
 * Pure row-mapping function — one branch per `ListRow["entity_type"]`,
 * per the plan's row-mapping table. Takes a `TFunction` rather than reading
 * the `i18n` singleton directly, mirroring `quick-view-fields.ts`'s own
 * `toFieldRows(qv, t)` precedent for the same reason: an injected
 * translator keeps this helper testable and consistent with the app's i18n
 * convention (no hardcoded user-facing copy) without coupling it to a
 * mounted `I18nextProvider`.
 */
export function toDisplayRow(
  row: ListRow,
  t: TFunction
): GlobalMapListDisplayRow {
  switch (row.entity_type) {
    case "lead":
    case "project":
      return projectOrLeadDisplayRow(row, t)
    case "subcontractor":
      return subcontractorDisplayRow(row, t)
    case "appointment":
      return appointmentDisplayRow(row, t)
  }
}
